import React, { useEffect, useState } from "react";
import { createInvite, listInvites, revokeInvite } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, Trash2, UserPlus, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const inviteLink = (token) => `${window.location.origin}/invite/${token}`;

const statusStyle = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-slate-100 text-slate-500 border-slate-200",
  accepted: "bg-blue-50 text-[#002FA7] border-blue-200",
};

const InviteManager = ({ open, onOpenChange }) => {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastLink, setLastLink] = useState("");

  const load = async () => {
    try { setInvites(await listInvites()); } catch (e) {}
  };

  useEffect(() => { if (open) { load(); setLastLink(""); } }, [open]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const inv = await createInvite({ email: email || null, name: name || null });
      const link = inviteLink(inv.token);
      setLastLink(link);
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Invite link created & copied");
      setEmail(""); setName("");
      load();
    } catch (e) {
      toast.error("Could not create invite");
    } finally {
      setCreating(false);
    }
  };

  const copy = (token) => {
    navigator.clipboard.writeText(inviteLink(token));
    toast.success("Invite link copied");
  };

  const handleRevoke = async (id) => {
    await revokeInvite(id);
    toast.success("Invite revoked");
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]" data-testid="invite-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Invite Team Members</DialogTitle>
          <DialogDescription>Generate a single-use invite link. Links expire automatically after 14 days.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-caps">Name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" className="mt-1.5 text-sm" data-testid="invite-create-name" />
            </div>
            <div>
              <Label className="label-caps">Email (optional)</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@soverenx.com" className="mt-1.5 text-sm" data-testid="invite-create-email" />
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating} className="w-full gap-2 text-white" style={{ background: "#002FA7" }} data-testid="invite-create-btn">
            {creating ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Generate Invite Link
          </Button>
          {lastLink && (
            <div className="flex items-center gap-2 bg-slate-50 rounded-md p-2 border border-slate-200">
              <input readOnly value={lastLink} className="flex-1 bg-transparent text-xs font-mono text-slate-600 outline-none" data-testid="invite-last-link" />
              <button onClick={() => { navigator.clipboard.writeText(lastLink); toast.success("Copied"); }} className="text-[#002FA7]" data-testid="invite-copy-last"><Copy size={15} /></button>
            </div>
          )}
        </div>

        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {invites.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No invites yet.</p>
          ) : invites.map((i) => (
            <div key={i.id} className="flex items-center justify-between border border-slate-200 rounded-md p-2.5" data-testid={`invite-row-${i.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{i.email || i.name || "Anyone with the link"}</div>
                <div className="text-[11px] text-slate-400">Expires {new Date(i.expires_at).toLocaleDateString()}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${statusStyle[i.status]}`}>{i.status}</span>
                {i.status === "active" && (
                  <button onClick={() => copy(i.token)} className="text-slate-400 hover:text-[#002FA7]" data-testid={`invite-copy-${i.id}`}><Copy size={15} /></button>
                )}
                <button onClick={() => handleRevoke(i.id)} className="text-slate-400 hover:text-red-600" data-testid={`invite-revoke-${i.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteManager;
